// One card's controls as its zones read them (#6753): `MissionCard` makes the card's
// controller (`./card-controls.ts`, `./epic-controls.ts`) under the page's ONE flood guard and
// provides it here, so the Now and Mission zones, every job row and every epic row reach the
// same one without threading it through four components. `on()` is the gate: an admin
// (`canAdminWrite`, owner ruling G21) on this org's own machine (G23). A member, a viewer or
// another org's machine reads `on() === false` and is shown no control and no write item (read
// reactively: a role settles after first paint); outside a Fleet page (no guard) there is no
// handle at all.

import { createContext, useContext } from 'solid-js';
import type { CardControls } from './card-controls';
import type { EpicControls } from './epic-controls';
import type { UtilJob } from './fleetUtilization';
import type { KeyRow } from './keys-types';
import { type JobTag, jobTag } from './machine-jobs';

export type CardControlsHandle = {
  readonly card: CardControls;
  readonly epics: EpicControls;
  /** An admin on an own-org machine: the controls show. */
  readonly on: () => boolean;
  /** The account's keys for the epic model picker; undefined until read (no picker yet). */
  readonly keys: () => readonly KeyRow[] | undefined;
};

export const CardControlsContext = createContext<CardControlsHandle | null>(null);

/** A job's tag: the controls' view of it when they show (`shownJobTag`), else the row's own. */
export function tagOfJob(handle: CardControlsHandle | null, job: UtilJob): JobTag | null {
  return handle?.on() === true ? handle.card.tagOf(job) : jobTag(job);
}

/** The card's controls handle, or null outside a Fleet page. Gate rendering on its `on()`. */
export function useCardControls(): CardControlsHandle | null {
  return useContext(CardControlsContext) ?? null;
}
